import { createContext, FC, useContext, useEffect, useState, ReactNode } from 'react';
import { WithChildren } from '../../../../_metronic/helpers';
import { getAllCompannies } from './_requests';

type Company = {
  id: string;
  company_name: string;
  default: boolean;
  company_id : string
};

type CompaniesContextProps = {
  companies: Company[];
  defaultCompany: Company | null;
  updateDefaultCompany: (companyId: string) => void;
  saveCompaniesLocally: (companies: []) => void;
  fetchCompaniesFromAPI: () => Promise<void>; // New function added
};

const initialCompanies: Company[] = []; // Set your initial companies here

const CompaniesContext = createContext<CompaniesContextProps | undefined>(undefined);

const CompaniesProvider: FC<WithChildren> = ({children}) =>  {
  const [companies, setCompanies] = useState<Company[]>(initialCompanies);
  const [defaultCompany, setDefaultCompany] = useState<Company | null>(null);

  const updateDefaultCompany = (companyId: string) => {
    const updatedCompanies = companies.map((company) => ({
      ...company,
      default: company.company_id === companyId,
    }));
    localStorage.setItem('companies', JSON.stringify(updatedCompanies));
    setCompanies(updatedCompanies);
  };

  const saveCompaniesLocally = (companies: Company[]) => {
    localStorage.setItem('companies', JSON.stringify(companies));
    setCompanies(companies);
    // const defaultCompany = companies.find((company) => company.default) || null;
    // setDefaultCompany(defaultCompany);
  };

  useEffect(() => {
    // Load companies from local storage on component mount
    const storedCompanies = JSON.parse(localStorage.getItem('companies') || '[]');
    setCompanies(storedCompanies);
  }, []);

  useEffect(() => {
    // Set the default company on component mount or when companies change
    const defaultCompany = companies.find((company) => company.default) || null;
    setDefaultCompany(defaultCompany);
  }, [companies]);

  const fetchCompaniesFromAPI = async () => {
    try {
      const companies = await getAllCompannies(); // Replace with your actual API endpoint
      const fetchedCompanies: Company[] = companies.map((company: any) => ({
        id: company._id,
        company_name: company.name,
        default: company._id === defaultCompany?.company_id,
        company_id: company._id,
      }));
      saveCompaniesLocally(fetchedCompanies);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  return (
    <CompaniesContext.Provider value={{ companies, defaultCompany, updateDefaultCompany, saveCompaniesLocally, fetchCompaniesFromAPI  }}>
      {children}
    </CompaniesContext.Provider>
  );
};

const useCompanies = () => {
  const context = useContext(CompaniesContext);
  if (!context) {
    throw new Error('useCompanies must be used within a CompaniesProvider');
  }
  return context;
};

export { CompaniesProvider, useCompanies };
